import type {
  CronRunRecord,
  ListCronRunsResponse,
  TriggerCronHandlerResponse,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

const BASE = '/v1/ops/cron-runs';

export interface CronRunFilters {
  readonly since?: string;
  readonly handler?: string;
  readonly limit?: number;
}

function toQueryString(f: CronRunFilters): string {
  const params = new URLSearchParams();
  if (f.since !== undefined && f.since.length > 0) params.set('since', f.since);
  if (f.handler !== undefined && f.handler.length > 0) params.set('handler', f.handler);
  if (f.limit !== undefined) params.set('limit', String(f.limit));
  const s = params.toString();
  return s.length > 0 ? `?${s}` : '';
}

export async function listCronRuns(
  filters: CronRunFilters,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<CronRunRecord[]> {
  const response = await opsApiRequest<ListCronRunsResponse>(
    `${BASE}${toQueryString(filters)}`,
    { signal },
    fetchImpl,
  );
  return response.runs;
}

export function cronRunsQueryKey(
  filters: CronRunFilters,
): readonly ['cron-runs', CronRunFilters] {
  return ['cron-runs', filters] as const;
}

export function useCronRuns(
  filters: CronRunFilters,
): UseQueryResult<CronRunRecord[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: cronRunsQueryKey(filters),
    queryFn: ({ signal }) => listCronRuns(filters, fetchImpl, signal),
    // Refetch every 30s on active mount — the cron writes at least once
    // per minute so fresh data lands while ops watches an active run.
    refetchInterval: 30_000,
  });
}

export async function triggerCronHandler(
  handler: string,
  fetchImpl: typeof fetch,
  nowMs?: number,
): Promise<TriggerCronHandlerResponse> {
  return opsApiRequest<TriggerCronHandlerResponse>(
    `/v1/ops/cron/${encodeURIComponent(handler)}/run`,
    {
      method: 'POST',
      ...(nowMs === undefined ? {} : { body: { now_ms: nowMs } }),
    },
    fetchImpl,
  );
}

/**
 * Manual control-loop re-run (epic #853 / #862). Mutation-shaped so the UI
 * treats it as an operator-triggered action. Settling invalidates the
 * cron-runs list because ops-api records a best-effort history row for
 * every manual trigger — the refreshed feed usually shows it right away,
 * but the mutation's own response stays the source of truth.
 */
export function useTriggerCronHandler(): UseMutationResult<
  TriggerCronHandlerResponse,
  Error,
  { readonly handler: string; readonly nowMs?: number }
> {
  const { fetchImpl } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => triggerCronHandler(input.handler, fetchImpl, input.nowMs),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['cron-runs'] });
    },
  });
}
